import { PlusOutlined } from "@ant-design/icons";
import { faClockRotateLeft, faEye, faPencil, faSquareCheck, faSquareXmark, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { TableColumnsType } from "antd";
import { Button, Tooltip } from "antd";
import { ProhibitedArea } from "components";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import SlideoutMenu from "components/atoms/SlideoutMenu";
import DataTable from "components/molecules/DataTable";
import React from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

interface IBusinessAreaDataListProps {
    businessAreaDataList?: IBusinessAreaData[];
    onCreateNewBusinessAreaData: () => void;
    onDeleteBusinessAreaData: (businessAreaData: IBusinessAreaData) => void;
    onViewBusinessAreaData: (dataDomainId: string) => void;
    onEditBusinessAreaData: (dataDomainId: string) => void;
    onViewBusinessAreaDataHistory: (dataDomainId: string) => void;
    permission?: IPermissionState;
}

const BusinessAreaDataList = ({
    businessAreaDataList = [],
    onCreateNewBusinessAreaData,
    onDeleteBusinessAreaData,
    onViewBusinessAreaData,
    onEditBusinessAreaData,
    onViewBusinessAreaDataHistory,
    permission,
}: IBusinessAreaDataListProps) => {
    const columns: TableColumnsType<IBusinessAreaData> = React.useMemo(
        () => [
            {
                fixed: "left",
                width: "40px",
                render: (_, businessAreaData) => (
                    <SlideoutMenu>
                        {permission?.canEdit && (
                            <Tooltip title="Edit Business Area Data">
                                <Button
                                    data-testid={CypressTestIds.BUSINESS_AREA_DATA_LIST_EDIT_BUTTON}
                                    type="link"
                                    disabled={!permission?.canEdit}
                                    onClick={() => (permission?.canEdit ? onEditBusinessAreaData(businessAreaData.dataDomainId) : null)}
                                    icon={<FontAwesomeIcon icon={faPencil} />}
                                />
                            </Tooltip>
                        )}
                        {permission?.canView && (
                            <Tooltip title="View Business Area Data">
                                <Button
                                    data-testid={CypressTestIds.BUSINESS_AREA_DATA_LIST_VIEW_BUTTON}
                                    type="link"
                                    disabled={!permission?.canView}
                                    onClick={() => (permission?.canView ? onViewBusinessAreaData(businessAreaData.dataDomainId) : null)}
                                    icon={<FontAwesomeIcon icon={faEye} />}
                                />
                            </Tooltip>
                        )}
                        {permission?.canView && (
                            <Tooltip title="View Business Area Data History">
                                <Button
                                    data-testid={CypressTestIds.BUSINESS_AREA_DATA_LIST_VIEW_HISTORY_BUTTON}
                                    type="link"
                                    disabled={!permission?.canView}
                                    onClick={() => (permission?.canView ? onViewBusinessAreaDataHistory(businessAreaData.dataDomainId) : null)}
                                    icon={<FontAwesomeIcon icon={faClockRotateLeft} />}
                                />
                            </Tooltip>
                        )}
                        {(permission?.canEdit || permission?.canView) && permission?.canDelete && <div className="block h-6 w-0 border-r border-dashed border-gray-300" />}
                        {permission?.canDelete && (
                            <Tooltip title="Delete Business Area Data">
                                <Button
                                    data-testid={CypressTestIds.BUSINESS_AREA_DATA_LIST_DELETE_BUTTON}
                                    type="link"
                                    danger
                                    disabled={!permission?.canDelete}
                                    onClick={() => (permission?.canDelete ? onDeleteBusinessAreaData(businessAreaData) : null)}
                                    icon={<FontAwesomeIcon icon={faTrashCan} />}
                                />
                            </Tooltip>
                        )}
                    </SlideoutMenu>
                ),
            },
            {
                width: "150px",
                dataIndex: "businessAreaName",
                title: "Business Area",
                ellipsis: {
                    showTitle: false,
                },
                render: (value: IBusinessAreaData["businessAreaName"]) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: "100px",
                dataIndex: "domainName",
                title: "Domain",
                ellipsis: {
                    showTitle: false,
                },
                render: (value: IBusinessAreaData["domainName"]) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: "250px",
                dataIndex: "domainDescription",
                title: "Domain Description",
                ellipsis: {
                    showTitle: false,
                },
                render: (value: IBusinessAreaData["domainDescription"]) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: "130px",
                dataIndex: "domainPurpose",
                title: "Domain Purpose",
                ellipsis: {
                    showTitle: false,
                },
                render: (value: IBusinessAreaData["domainPurpose"]) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: "130px",
                dataIndex: "usedForTagging",
                title: "Used for tagging",
                ellipsis: {
                    showTitle: false,
                },
                render: (value: IBusinessAreaData["usedForTagging"]) =>
                    value ? <FontAwesomeIcon icon={faSquareCheck} className="text-green-600" /> : <FontAwesomeIcon icon={faSquareXmark} className="text-gray-400" />,
            },
            {
                width: "80px",
                title: "Records",
                render: (_, businessAreaData) => <>{businessAreaData.recordCount || 0}</>,
            },
        ],
        [onDeleteBusinessAreaData, onEditBusinessAreaData, onViewBusinessAreaData, onViewBusinessAreaDataHistory, permission?.canDelete, permission?.canEdit, permission?.canView],
    );

    if (!permission) {
        return <ProhibitedArea></ProhibitedArea>;
    }

    return (
        <FlexContainerLayout
            footerClassName="justify-end"
            footer={
                <Button type="primary" disabled={!permission.canAdd} onClick={onCreateNewBusinessAreaData} data-testid={CypressTestIds.BUSINESS_AREA_DATA_ADD_NEW_BUTTON}>
                    <PlusOutlined /> Add Business Area Data
                </Button>
            }
        >
            <DataTable<IBusinessAreaData>
                dataTestId={CypressTestIds.BUSINESS_AREA_DATA_LIST_TABLE}
                showHeader={false}
                hideSelectionColumn
                tableProps={{
                    rowKey: "dataDomainId",
                    dataSource: businessAreaDataList,
                    pagination: false,
                    columns,
                }}
            />
        </FlexContainerLayout>
    );
};

export default BusinessAreaDataList;
